import React, { useEffect, useState } from 'react';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import leaveApi from './leaveApi';

const LeaveRequestDialog = ({
  request,
  isOpen,
  onClose,
  onApprove,
  onReject,
}) => {
  const [remarks, setRemarks] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [requesterInfo, setRequesterInfo] = useState(null);
  const [isLoadingRequesterInfo, setIsLoadingRequesterInfo] = useState(false);
  const [roleInfo, setRoleInfo] = useState(null);
  const [isLoadingRoleInfo, setIsLoadingRoleInfo] = useState(false);

  useEffect(() => {
    const fetchRequesterInfo = async () => {
      if (!isOpen || !request?.raised_by) {
        setRequesterInfo(null);
        setRoleInfo(null);
        return;
      }

      setIsLoadingRequesterInfo(true);
      try {
        const userInfo = await leaveApi.getUserInfo(request.raised_by);
        setRequesterInfo(userInfo);

        // Fetch role information if spec_role or role_name is available
        const roleToFetch = userInfo?.spec_role || userInfo?.role_name;
        if (roleToFetch) {
          setIsLoadingRoleInfo(true);
          try {
            const roleData = await leaveApi.getRoleInfo(roleToFetch);
            setRoleInfo(roleData);
          } catch (roleError) {
            console.error('Error fetching role info:', roleError);
            setRoleInfo(null);
          } finally {
            setIsLoadingRoleInfo(false);
          }
        }
      } catch (error) {
        console.error('Error fetching requester info:', error);
        // Fallback to just the user ID
        setRequesterInfo({ user_id: request.raised_by });
      } finally {
        setIsLoadingRequesterInfo(false);
      }
    };

    fetchRequesterInfo();
  }, [isOpen, request?.raised_by]);

  if (!isOpen || !request) return null;

  const formattedDates = request.date_list
    ? request.date_list.map(date =>
        leaveApi.formatDateForDisplay(new Date(date))
      )
    : [];

  const formatRequestTime = raisedAt => {
    if (!raisedAt) return 'Unknown';
    const date = new Date(raisedAt);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleApprove = async () => {
    if (!remarks.trim()) {
      setError('Please provide remarks for approval');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const response = await leaveApi.updateLeaveRequest(
        request.request_id,
        'approved',
        remarks.trim()
      );
      
      // Log successful API call
      loggingService.logInfo(
        'leaveApproveRequest',
        'Successfully approved leave request',
        {
          endpoint: `/leave/manager/decision/${request.request_id}`,
          method: 'PUT',
          requestId: request.request_id,
          status: 'approved',
          leaveType: request.leave_type,
          numberOfDays: request.number_of_days,
        }
      ).catch(logError => {
        console.warn('[LeaveRequestDialog] Failed to log event:', logError);
      });
      
      onApprove && onApprove(request);
      onClose();
    } catch (error) {
      console.error('Error approving leave request:', error);
      setError(error.message || 'Failed to approve leave request');
      // Error logging is handled in leaveApi.js
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!remarks.trim()) {
      setError('Please provide remarks for rejection');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const response = await leaveApi.updateLeaveRequest(
        request.request_id,
        'rejected',
        remarks.trim()
      );
      
      // Log successful API call
      loggingService.logInfo(
        'leaveRejectRequest',
        'Successfully rejected leave request',
        {
          endpoint: `/leave/manager/decision/${request.request_id}`,
          method: 'PUT',
          requestId: request.request_id,
          status: 'rejected',
          leaveType: request.leave_type,
          numberOfDays: request.number_of_days,
        }
      ).catch(logError => {
        console.warn('[LeaveRequestDialog] Failed to log event:', logError);
      });
      
      onReject && onReject(request);
      onClose();
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      setError(error.message || 'Failed to reject leave request');
      // Error logging is handled in leaveApi.js
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setRemarks('');
      setError('');
      onClose();
    }
  };

  const handleOverlayClick = event => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleCardClick = event => {
    event.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-hidden"
      onMouseDown={handleOverlayClick}
    >
      <Card.Light
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white/95 border-0 shadow-xl rounded-lg"
        onMouseDown={handleCardClick}
      >
          <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className={typography.h3} style={{ color: '#000000' }}>
                Leave Request Details
              </h3>
              <p className={typography.p} style={{ color: '#666666', marginTop: '0.5rem' }}>
                Review and make a decision on this leave request
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              ❌ {error}
            </div>
          )}

          {/* Request Details */}
          <div className="space-y-5 mb-6">
            {/* Status and Basic Info */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  ⏳ Pending
                </span>
                <span className="text-sm text-gray-500">
                  {request.number_of_days} day{request.number_of_days > 1 ? 's' : ''}
                </span>
              </div>
              <span className="text-sm text-gray-500">
                {formatRequestTime(request.raised_at)}
              </span>
            </div>

            {/* Leave Type */}
            <div>
              <h6 className={typography.h6} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                Leave Type
              </h6>
              <p className={typography.p} style={{ color: '#666666' }}>
                {request.leave_type}
              </p>
            </div>

            {/* Reason */}
            <div>
              <h6 className={typography.h6} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                Reason
              </h6>
              <p className={typography.p} style={{ color: '#666666' }}>
                {request.reason || 'No reason provided'}
              </p>
            </div>

            {/* Requested Dates */}
            <div>
              <h6 className={typography.h6} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                Requested Dates
              </h6>
              <div className="flex flex-wrap gap-2">
                {formattedDates.map((date, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-lg font-medium"
                  >
                    {date}
                  </span>
                ))}
              </div>
            </div>

            {/* Requester Information */}
            <div>
              <h6 className={typography.h6} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                Requested By
              </h6>
              {isLoadingRequesterInfo ? (
                <p className={typography.p} style={{ color: '#666666' }}>
                  Loading user information...
                </p>
              ) : requesterInfo ? (
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                      Name
                    </p>
                    <p className={typography.p} style={{ color: '#666666' }}>
                      {requesterInfo.user_name || requesterInfo.name || 'N/A'}
                    </p>
                  </div>
                  {requesterInfo.employee_id && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                        Employee ID
                      </p>
                      <p className={typography.p} style={{ color: '#666666' }}>
                        {requesterInfo.employee_id}
                      </p>
                    </div>
                  )}
                  {(requesterInfo.spec_role || requesterInfo.role_name) && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                        Role
                      </p>
                      {isLoadingRoleInfo ? (
                        <p className={typography.p} style={{ color: '#666666' }}>
                          Loading role information...
                        </p>
                      ) : (
                        <p className={typography.p} style={{ color: '#666666' }}>
                          {roleInfo?.display_name ||
                            roleInfo?.role_name ||
                            requesterInfo.spec_role ||
                            requesterInfo.role_name ||
                            'N/A'}
                        </p>
                      )}
                    </div>
                  )}
                  {requesterInfo.email && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                        Email
                      </p>
                      <p className={typography.p} style={{ color: '#666666' }}>
                        {requesterInfo.email}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className={typography.p} style={{ color: '#666666' }}>
                  {request.raised_by || 'Unknown'}
                </p>
              )}
            </div>
          </div>

          {/* Remarks Input */}
          <div className="mb-6">
            <h6 className={typography.h6} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              Your Remarks *
            </h6>
            <textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder="Please provide your remarks for this decision..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              disabled={isProcessing}
            />
            <p className="text-sm text-gray-500 mt-1">
              This will be visible to the employee
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <button
              onClick={handleReject}
              disabled={isProcessing || !remarks.trim()}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  ❌ Reject
                </>
              )}
            </button>
            <button
              onClick={handleApprove}
              disabled={isProcessing || !remarks.trim()}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  ✅ Approve
                </>
              )}
            </button>
          </div>
          </div>
        </Card.Light>
    </div>
  );
};

export default LeaveRequestDialog;
